// client/src/routes/__root.tsx — 04 §9.2: outlet + errorComponent (SCR-SYS-500)
// + notFoundComponent (SCR-SYS-404).
//
// UI-001 rendered these as unstyled scaffolding, UI-002 gave them the brand theme, I18N-001
// moved their copy into romanian.json, and UI-003 finishes them: the developer-facing
// `error.message` becomes an RFC 9457 read carrying the correlation id §5.11 requires.
//
// WHAT STAYS OUTSIDE THE SHELL, AND WHY. §8.20 says all three system screens render inside the
// active shell — and above `/app` there is no active shell to be inside. A URL like `/typo`
// never reached an authenticated area, so it gets the centred card; the same two states inside
// `/app/*` are handled by `app/route.tsx`, which renders them in its own `<Outlet />` with the
// header and sidebar still in place.

import type { ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { Link, Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { toProblemDetails } from '@/api/problem-details';
import { SystemState } from '@/components/ui/states';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  return <Outlet />;
}

/** The frame for a system state reached outside the shell — one place to restyle, two callers. */
function UnshelledState({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-base-200 p-6">
      <div className="w-full max-w-xl">{children}</div>
    </main>
  );
}

function ErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation();
  const problem = toProblemDetails(error);
  return (
    <UnshelledState>
      <SystemState
        icon="exclamation-triangle"
        title={t('errors.title')}
        body={problem.detail ?? t('errors.generic')}
        correlationId={problem.correlationId}
        actions={
          <Link to="/" className="btn btn-primary">
            {t('common.backHome')}
          </Link>
        }
      />
    </UnshelledState>
  );
}

function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <UnshelledState>
      <SystemState
        icon="question-mark-circle"
        title={t('errors.notFoundTitle')}
        body={t('errors.notFoundBody')}
        actions={
          <Link to="/" className="btn btn-primary">
            {t('common.backHome')}
          </Link>
        }
      />
    </UnshelledState>
  );
}
